import React, { useState } from 'react';
import { 
  Navigation, MapPin, Phone, Clock, Package, CheckCircle2, 
  ChevronLeft, AlertCircle, MessageSquare, Camera, RotateCcw,
  Truck, User, DollarSign, ArrowRight
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

// Mock delivery data
const mockCurrentDelivery = {
  id: 'ORD-001',
  customer: {
    name: 'John Smith',
    phone: '+1 (613) 555-0123',
    address: '123 Bank Street, Ottawa, ON K1P 5N2',
    instructions: 'Ring doorbell twice. Leave at door if no answer.'
  },
  items: [
    { name: 'Knorr seasoning cubes', qty: 2, price: 4.00 },
    { name: 'NutriFresh Mango Juice', qty: 1, price: 5.99 },
    { name: 'Goya AP seasoning', qty: 3, price: 11.97 },
  ],
  total: 21.96,
  paymentMethod: 'Card',
  status: 'in_transit',
  estimatedTime: '15 mins',
  distance: '3.2 km',
  pickupTime: '2:15 PM',
};

const mockDeliveryQueue = [
  {
    id: 'ORD-002',
    customer: 'Sarah Johnson',
    address: '456 Rideau Street',
    items: 3,
    total: 28.50,
    status: 'ready'
  },
  {
    id: 'ORD-003',
    customer: 'Mike Wilson',
    address: '789 Carling Avenue',
    items: 8,
    total: 89.99,
    status: 'preparing'
  }
];

const DriversNavigation = ({ onBack }) => {
  const [currentDelivery, setCurrentDelivery] = useState(mockCurrentDelivery);
  const [deliveryStatus, setDeliveryStatus] = useState('in_transit');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [deliveryNote, setDeliveryNote] = useState('');

  const handleStatusUpdate = (newStatus) => {
    setDeliveryStatus(newStatus);
    
    if (newStatus === 'arrived') {
      toast.success('Customer has been notified of your arrival');
    } else if (newStatus === 'delivered') {
      setShowConfirmation(true);
    }
  };

  const confirmDelivery = () => {
    toast.success('Delivery completed! Moving to next order.');
    setShowConfirmation(false);
    setDeliveryStatus('in_transit');
    // In real app, would fetch next delivery
  };

  const openNavigation = () => {
    // Open Google Maps with the address
    const address = encodeURIComponent(currentDelivery.customer.address);
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${address}`, '_blank');
  };

  const callCustomer = () => {
    window.open(`tel:${currentDelivery.customer.phone}`, '_self');
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={onBack} className="p-2">
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <Navigation className="w-6 h-6 text-blue-500" />
              Active Delivery
            </h1>
            <p className="text-sm text-gray-500">Order {currentDelivery.id}</p>
          </div>
        </div>
        <Badge className={
          deliveryStatus === 'in_transit' ? 'bg-blue-100 text-blue-700' :
          deliveryStatus === 'arrived' ? 'bg-yellow-100 text-yellow-700' :
          'bg-green-100 text-green-700'
        }>
          {deliveryStatus === 'in_transit' ? 'In Transit' :
           deliveryStatus === 'arrived' ? 'Arrived' : 'Delivered'}
        </Badge>
      </div>

      {/* Map Placeholder */}
      <Card className="mb-6 overflow-hidden">
        <div className="relative bg-gradient-to-br from-blue-100 to-green-100 h-64 flex items-center justify-center">
          <div className="text-center">
            <MapPin className="w-16 h-16 text-blue-500 mx-auto mb-4 animate-bounce" />
            <p className="text-gray-600 mb-4">Interactive Map</p>
            <Button onClick={openNavigation} className="bg-blue-500 hover:bg-blue-600 text-white">
              <Navigation className="w-4 h-4 mr-2" />
              Open in Google Maps
            </Button>
          </div>
          
          {/* Route info overlay */}
          <div className="absolute bottom-4 left-4 right-4 flex justify-between">
            <div className="bg-white/90 backdrop-blur-sm px-4 py-2 rounded-lg shadow">
              <p className="text-xs text-gray-500">Distance</p>
              <p className="font-bold text-gray-800">{currentDelivery.distance}</p>
            </div>
            <div className="bg-white/90 backdrop-blur-sm px-4 py-2 rounded-lg shadow">
              <p className="text-xs text-gray-500">ETA</p>
              <p className="font-bold text-gray-800">{currentDelivery.estimatedTime}</p>
            </div>
          </div>
        </div>
      </Card>

      {/* Customer Info */}
      <Card className="p-6 mb-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <User className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-800">{currentDelivery.customer.name}</h3>
              <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                <MapPin className="w-4 h-4" />
                {currentDelivery.customer.address}
              </p>
              {currentDelivery.customer.instructions && (
                <div className="mt-2 p-2 bg-yellow-50 rounded-lg">
                  <p className="text-xs text-yellow-700 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {currentDelivery.customer.instructions}
                  </p>
                </div>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={callCustomer}>
              <Phone className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm">
              <MessageSquare className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </Card>

      {/* Order Summary */}
      <Card className="p-6 mb-6">
        <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <Package className="w-5 h-5 text-green-500" />
          Order Items
        </h3>
        <div className="space-y-3">
          {currentDelivery.items.map((item, index) => (
            <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
              <div>
                <p className="font-medium text-gray-800">{item.name}</p>
                <p className="text-sm text-gray-500">Qty: {item.qty}</p>
              </div>
              <p className="font-semibold text-gray-800">${item.price.toFixed(2)}</p>
            </div>
          ))}
        </div>
        <div className="mt-4 pt-4 border-t border-gray-200 flex justify-between items-center">
          <div>
            <p className="text-sm text-gray-500">Payment: {currentDelivery.paymentMethod}</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500">Total</p>
            <p className="text-xl font-bold text-gray-800">${currentDelivery.total.toFixed(2)}</p>
          </div>
        </div>
      </Card>

      {/* Status Actions */}
      <Card className="p-6 mb-6">
        <h3 className="font-semibold text-gray-800 mb-4">Update Delivery Status</h3>
        <div className="grid grid-cols-3 gap-4">
          <Button
            variant={deliveryStatus === 'in_transit' ? 'default' : 'outline'}
            onClick={() => handleStatusUpdate('in_transit')}
            className={deliveryStatus === 'in_transit' ? 'bg-blue-500 hover:bg-blue-600 text-white' : ''}
          >
            <Truck className="w-4 h-4 mr-2" />
            In Transit
          </Button>
          <Button
            variant={deliveryStatus === 'arrived' ? 'default' : 'outline'}
            onClick={() => handleStatusUpdate('arrived')}
            className={deliveryStatus === 'arrived' ? 'bg-yellow-500 hover:bg-yellow-600 text-white' : ''}
          >
            <MapPin className="w-4 h-4 mr-2" />
            Arrived
          </Button>
          <Button
            variant={deliveryStatus === 'delivered' ? 'default' : 'outline'}
            onClick={() => handleStatusUpdate('delivered')}
            className={deliveryStatus === 'delivered' ? 'bg-green-500 hover:bg-green-600 text-white' : ''}
          >
            <CheckCircle2 className="w-4 h-4 mr-2" />
            Delivered
          </Button>
        </div>
      </Card>

      {/* Delivery Queue */}
      <Card className="p-6">
        <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <Clock className="w-5 h-5 text-orange-500" />
          Upcoming Deliveries
        </h3>
        <div className="space-y-3">
          {mockDeliveryQueue.map((order) => (
            <div 
              key={order.id}
              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
            >
              <div className="flex items-center gap-3">
                <div className={`w-2 h-2 rounded-full ${
                  order.status === 'ready' ? 'bg-green-500' : 'bg-orange-500'
                }`} />
                <div>
                  <p className="font-medium text-gray-800">{order.id}</p>
                  <p className="text-sm text-gray-500">{order.customer} • {order.address}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="font-semibold text-gray-800">${order.total.toFixed(2)}</p>
                <Badge className={
                  order.status === 'ready' 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-orange-100 text-orange-700'
                }>
                  {order.status === 'ready' ? 'Ready' : 'Preparing'}
                </Badge>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Delivery Confirmation Modal */}
      {showConfirmation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-md mx-4 p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800">Confirm Delivery</h3>
              <p className="text-gray-500 mt-2">Mark this order as delivered?</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Delivery Note (Optional)</label>
                <Input
                  value={deliveryNote}
                  onChange={(e) => setDeliveryNote(e.target.value)}
                  placeholder="Left at door, handed to customer, etc."
                  className="mt-1"
                />
              </div>

              <Button 
                variant="outline" 
                className="w-full flex items-center justify-center gap-2"
              >
                <Camera className="w-4 h-4" />
                Take Proof Photo
              </Button>
            </div>

            <div className="flex gap-3 mt-6">
              <Button 
                variant="outline" 
                onClick={() => setShowConfirmation(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                onClick={confirmDelivery}
                className="flex-1 bg-green-500 hover:bg-green-600 text-white"
              >
                Confirm Delivery
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default DriversNavigation;
